import { useState, useRef, useCallback } from 'react'
import { Checkin, Feature, Project, getCheckins, getCheckinsByIds, getFeatures, getProjects, getRecentFeatures, FeatureStatus } from '../api/client'
import { reconstructDataFromNormalized } from '../utils/dataNormalization'
import { useNotify } from '../components/notice/useNotify'

interface UseCheckinsDataParams {
  // Optional filters - if not provided, will fetch all checkins
  featureId?: number | null
  projectId?: number | null
  textSearch?: string | null
  collectionItemId?: number | null
  sortOrder?: string
  startDate?: string
  endDate?: string
  limit?: number
  // When set, fetch only this checkin and ignore other filters.
  checkinId?: number | null
}

interface UseCheckinsDataReturn {
  // Data state
  checkins: Checkin[]
  features: Feature[]
  recentFeatures: Feature[]
  projects: Project[]
  collectionItems: Record<string, any>

  // Loading states
  loading: boolean
  searchLoading: boolean

  // Update tracking
  hasUpdatesAvailable: boolean
  setHasUpdatesAvailable: (hasUpdates: boolean) => void

  // Data operations
  fetchData: (isInitialLoad?: boolean) => Promise<void>
  setCheckins: React.Dispatch<React.SetStateAction<Checkin[]>>
  /** Merge a partial item map into the local cache. Used by parse/save paths
   * to avoid stale lookups after the backend creates new collection items
   * (e.g. resolver-bootstrapped Places, CREATE_NEW reference items) — without
   * waiting for the next fetchData. */
  mergeCollectionItems: (items: Record<string, any>) => void
  /** Add any Feature definitions whose id isn't already in the local cache.
   * Existing entries are left untouched (no spurious re-render on every
   * parse). Used by parse paths so that lazily-created system features (e.g.
   * the user-global Locations feature bootstrapped on first location parse)
   * become visible to render code that keys off the cache (chip rendering in
   * CheckinComposer goes through `[...recentFeatures, ...features]`) —
   * without waiting for fetchData. */
  mergeFeatures: (features: Feature[]) => void
  checkForUpdates: () => Promise<void>

  // Refs for external access
  checkinsRef: React.MutableRefObject<Checkin[]>
}

/**
 * Hook that manages checkin data storage patterns extracted from CheckinsPage.
 * Provides reusable data fetching, caching, and update detection logic.
 */
export const useCheckinsData = (params: UseCheckinsDataParams = {}): UseCheckinsDataReturn => {
  const {
    featureId,
    projectId,
    textSearch,
    collectionItemId,
    sortOrder = textSearch ? 'most_relevant' : 'most_recent',
    startDate,
    endDate,
    limit = 100,
    checkinId
  } = params

  // Core data state - extracted from CheckinsPage
  const [checkins, setCheckins] = useState<Checkin[]>([])
  const [features, setFeatures] = useState<Feature[]>([])
  const [recentFeatures, setRecentFeatures] = useState<Feature[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [collectionItems, setCollectionItems] = useState<Record<string, any>>({})

  // Loading states - extracted from CheckinsPage
  const [loading, setLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)

  // A failed load is raised on the app-wide notice.
  const notify = useNotify()

  // Update tracking - extracted from CheckinsPage
  const [hasUpdatesAvailable, setHasUpdatesAvailable] = useState(false)
  const renderTimestampRef = useRef<Date | null>(null)
  const checkinsRef = useRef<Checkin[]>(checkins)

  // Keep ref in sync - pattern from CheckinsPage
  checkinsRef.current = checkins

  // Main data fetching function - extracted and generalized from CheckinsPage
  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true)
    } else {
      setSearchLoading(true)
    }

    try {
      const [fetchedFeatures, fetchedProjects, checkinsResponse, fetchedRecentFeatures] = await Promise.all([
        getFeatures(FeatureStatus.ACTIVE),
        getProjects(),
        checkinId
          ? getCheckinsByIds([checkinId])
          : getCheckins(
              featureId || undefined,
              projectId || undefined,
              limit,
              textSearch || undefined,
              sortOrder,
              startDate || undefined,
              endDate || undefined,
              collectionItemId || undefined
            ),
        getRecentFeatures()
      ])

      setFeatures(fetchedFeatures)
      setProjects(fetchedProjects)
      setRecentFeatures(fetchedRecentFeatures)

      // Reconstruct checkins and collection items from normalized data
      const { checkins, collectionItems } = reconstructDataFromNormalized(checkinsResponse)
      setCheckins(checkins)
      setCollectionItems(collectionItems)

      // Set render timestamp for update detection - pattern from CheckinsPage
      const newRenderTimestamp = new Date()
      renderTimestampRef.current = newRenderTimestamp
      setHasUpdatesAvailable(false)

    } catch (err) {
      notify.error('Failed to load data')
      console.error(err)
    } finally {
      if (isInitialLoad) {
        setLoading(false)
      } else {
        setSearchLoading(false)
      }
    }
  }, [featureId, projectId, textSearch, collectionItemId, sortOrder, startDate, endDate, limit, checkinId, notify])

  // Merge new collection items into the cache. The backend's parse and
  // get-checkin responses include any items referenced by the (possibly
  // freshly-attached) checkin_features rows. Without this merge, references
  // to newly-created items render with no resolved name until the next
  // full fetchData.
  const mergeCollectionItems = useCallback((items: Record<string, any>) => {
    if (!items || Object.keys(items).length === 0) return
    setCollectionItems((prev) => ({ ...prev, ...items }))
  }, [])

  const mergeFeatures = useCallback((incoming: Feature[]) => {
    if (!incoming || incoming.length === 0) return
    setFeatures((prev) => {
      const knownIds = new Set(prev.map((f) => f.id))
      const additions = incoming.filter((f) => !knownIds.has(f.id))
      return additions.length > 0 ? [...prev, ...additions] : prev
    })
  }, [])

  // Check for updates function - extracted from CheckinsPage
  const checkForUpdates = useCallback(async () => {
    const currentRenderTimestamp = renderTimestampRef.current

    if (!currentRenderTimestamp) {
      return
    }

    try {
      const [currentFeatures, currentProjects] = await Promise.all([
        getFeatures(FeatureStatus.ACTIVE),
        getProjects()
      ])

      // Check if any feature or project has been updated since render timestamp
      const updatedFeatures = currentFeatures.filter(feature =>
        new Date(feature.updated_at) > currentRenderTimestamp
      )
      const updatedProjects = currentProjects.filter(project =>
        new Date(project.updated_at) > currentRenderTimestamp
      )

      const hasFeatureUpdates = updatedFeatures.length > 0
      const hasProjectUpdates = updatedProjects.length > 0

      if (hasFeatureUpdates || hasProjectUpdates) {
        setHasUpdatesAvailable(true)
      }
    } catch (err) {
      // Silently ignore update check errors - pattern from CheckinsPage
    }
  }, [])

  return {
    // Data state
    checkins,
    features,
    recentFeatures,
    projects,
    collectionItems,

    // Loading states
    loading,
    searchLoading,

    // Update tracking
    hasUpdatesAvailable,
    setHasUpdatesAvailable,

    // Data operations
    fetchData,
    setCheckins,
    mergeCollectionItems,
    mergeFeatures,
    checkForUpdates,

    // Refs for external access
    checkinsRef
  }
}
